import { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaEye } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  startIntegrationBridgeDashboard,
  startIntegrationBridgeView,
  startLoadIntegrationModuleRecord,
} from '../../app/actions/semanticIntegrationActions';
import { startRegisterUserInterest } from '../../app/actions/userInterestActions';
import PaginationFull from '../generic-ui/PaginationFull';
import Select from '../generic-ui/Select';
import './comparisonPanel.css';
import IntegrationFilterBar from './IntegrationFilterBar';
import IntegrationRecordViewModal from './IntegrationRecordViewModal';
import { MODULE_INTEGRATION_META } from './moduleIntegrationMeta';

const listOptions = [3, 6, 9, 12, 15, 18, 21, 30];

/**
 * Panel lateral ~85–90% con resultados integrados paginados + ojo → Preview.
 */
const IntegrationResultsPanel = ({
  isOpen,
  onClose,
  sourceModuleKey,
  targetModule,
  originItems = [],
  searchText = '',
  mode = 'M3',
  bridgeKind = 'dashboard', // 'dashboard' | 'view'
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [rawResults, setRawResults] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [pageSize, setPageSize] = useState(9);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [loadingRowId, setLoadingRowId] = useState(null);

  const targetMeta = MODULE_INTEGRATION_META[targetModule];
  const sourceLabel = t(MODULE_INTEGRATION_META[sourceModuleKey]?.labelKey || sourceModuleKey);
  const targetLabel = t(targetMeta?.labelKey || targetModule);

  const originKey = useMemo(
    () => (originItems || []).map((o) => o.id).join(','),
    [originItems]
  );

  useEffect(() => {
    if (!isOpen || !targetModule || !originKey) return undefined;

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setErrorMsg('');
      setRawResults([]);
      setAllResults([]);
      setCurrentPage(1);

      let resp;
      if (bridgeKind === 'view') {
        const first = originItems[0];
        resp = await dispatch(
          startIntegrationBridgeView({
            sourceModule: sourceModuleKey,
            sourceId: first.id,
            targetModule,
            searchText,
            mode,
          })
        );
        if (!cancelled) {
          if (resp?.ok && resp.targets?.[targetModule]?.ok) {
            const rows = resp.targets[targetModule].results || [];
            setRawResults(rows);
            setAllResults(rows);
          } else {
            setErrorMsg(
              resp?.targets?.[targetModule]?.msg ||
                resp?.msg ||
                t('semanticSearch.integration.error')
            );
          }
        }
      } else {
        resp = await dispatch(
          startIntegrationBridgeDashboard({
            sourceModule: sourceModuleKey,
            originItems,
            targetModule,
            searchText,
            mode,
          })
        );
        if (!cancelled) {
          if (resp?.ok) {
            const rows = resp.results || [];
            setRawResults(rows);
            setAllResults(rows);
          } else {
            setErrorMsg(resp?.msg || t('semanticSearch.integration.error'));
          }
        }
      }
      if (!cancelled) setLoading(false);
    };

    run();
    return () => {
      cancelled = true;
    };
    // originKey estabiliza el arreglo; originItems se lee del closure al abrir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, targetModule, sourceModuleKey, bridgeKind, searchText, mode, originKey, dispatch, t]);

  const totalItems = allResults.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize) || 1);
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return allResults.slice(start, start + pageSize);
  }, [allResults, currentPage, pageSize]);

  const onChangeSelect = (event) => {
    const size = parseInt(event.target.value, 10);
    setPageSize(size);
    setCurrentPage(1);
  };

  const onPageChange = (pageClick) => {
    setCurrentPage(pageClick);
  };

  const onEyeClick = async (row) => {
    setLoadingRowId(row.id);
    const resp = await dispatch(startLoadIntegrationModuleRecord(targetModule, row.id));
    setLoadingRowId(null);
    if (resp?.ok) {
      dispatch(
        startRegisterUserInterest({
          event_type: 'record_view',
          sourceModule: sourceModuleKey,
          targetModule,
          originIds: (originItems || []).map((o) => o.id),
          targetId: row.id,
          bridge_kind: bridgeKind,
          searchText,
          similarity_percent: row.similarity_percent,
        })
      );
      setViewModalOpen(true);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="comparison-panel integration-results-panel" style={{ width: '88vw' }}>
        <div className="panel-header">
          <h5 className="bridge-title">
            <span className="bridge-title-prefix">{t('semanticSearch.integration.bridgePrefix')}</span>
            {' '}
            <span className="bridge-title-source">
              {t(`semanticSearch.integration.bridgeName.${sourceModuleKey}`, { defaultValue: sourceLabel })}
            </span>
            {' '}
            <span className="bridge-title-with">{t('semanticSearch.integration.bridgeWith')}</span>
            {' '}
            <span className="bridge-title-target">
              {t(`semanticSearch.integration.bridgeName.${targetModule}`, { defaultValue: targetLabel })}
            </span>
          </h5>
          <button type="button" onClick={onClose} className="close-btn">
            ×
          </button>
        </div>

        <div className="panel-content">
          <div className="info-bar">
            {t('semanticSearch.integration.panelSummary', {
              count: totalItems,
              origins: originItems.length,
              target: targetLabel,
            })}
            {rawResults.length !== totalItems && rawResults.length > 0
              ? ` ${t('semanticSearch.integration.filterBar.filteredOf', {
                  filtered: totalItems,
                  total: rawResults.length,
                })}`
              : ''}
          </div>

          {!loading && !errorMsg && (
            <IntegrationFilterBar
              targetModule={targetModule}
              rawResults={rawResults}
              onFilteredResults={(rows) => {
                setAllResults(rows);
                setCurrentPage(1);
              }}
            />
          )}

          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}

          {!loading && errorMsg && <div className="text-danger mb-3">{errorMsg}</div>}

          {!loading && !errorMsg && (
            <>
              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 56 }}>{t('semanticSearch.integration.colView')}</th>
                      <th>#</th>
                      <th>ID</th>
                      <th>{t('semanticSearch.integration.colName')}</th>
                      <th>{t('semanticSearch.integration.colSimilarity')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          {t('semanticSearch.integration.empty')}
                        </td>
                      </tr>
                    )}
                    {pageRows.map((row, idx) => (
                      <tr key={`${row.targetModule || targetModule}-${row.id}`}>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            title={t('buttons.view')}
                            disabled={loadingRowId === row.id}
                            onClick={() => onEyeClick(row)}
                          >
                            {loadingRowId === row.id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <FaEye />
                            )}
                          </button>
                        </td>
                        <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td>{row.id}</td>
                        <td>{row.name || '—'}</td>
                        <td>
                          {row.similarity_percent != null
                            ? t('semanticSearch.similarityPercent', {
                                percent: row.similarity_percent,
                              })
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalItems > 0 && (
                <div className="mt-2">
                  <Select
                    listOptions={listOptions}
                    onChangeSelect={onChangeSelect}
                    pageSize={pageSize}
                  />
                  <div className="pagination justify-content-center">
                    <PaginationFull
                      totalRecords={totalItems}
                      itemsPerPage={pageSize}
                      pageNeighbours={2}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <IntegrationRecordViewModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        moduleKey={targetModule}
        sourceModuleKey={sourceModuleKey}
        originIds={(originItems || []).map((o) => o.id)}
        bridgeKind={bridgeKind}
      />
    </>
  );
};

export default IntegrationResultsPanel;
